import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import { uplogApi } from '@/api/uplog.api';
import { extractErrorMessage } from '@/lib/errors';

export function useUplogEnabled() {
  return useQuery({
    queryKey: ['uplog', 'status'],
    queryFn: () => uplogApi.getStatus(),
    select: (res) => res.data.data.enabled,
  });
}

export function useUplogCredential() {
  return useQuery({
    queryKey: ['uplog', 'credential'],
    queryFn: () => uplogApi.getCredential(),
    select: (res) => res.data.data,
    retry: false,
  });
}

export function useUpsertUplogCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uplogApi.upsertCredential,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['uplog', 'credential'] });
      toast.success('Credenciais salvas com sucesso');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useTestUplogAuth() {
  return useMutation({
    mutationFn: uplogApi.testAuth,
    onSuccess: (res) => {
      const { userDisplayName, userNicename, userRole } = res.data.data;
      toast.success(`Conexão OK — ${userDisplayName || userNicename} · perfil: ${userRole}`);
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useScanConfig() {
  return useQuery({
    queryKey: ['uplog', 'scan-config'],
    queryFn: () => uplogApi.getScanConfig(),
    select: (res) => res.data.data,
  });
}

export function useUpdateScanConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { scanHour?: string; isActive?: boolean }) =>
      uplogApi.updateScanConfig(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['uplog', 'scan-config'] });
      toast.success('Configuração salva');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useTriggerScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uplogApi.triggerScan,
    onSuccess: () => {
      toast.success('Scan iniciado');
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['uplog', 'pendencias'] });
        void qc.invalidateQueries({ queryKey: ['uplog', 'scan-config'] });
      }, 3000);
    },
    onError: (err) => {
      const status = (err as AxiosError).response?.status;
      if (status === 409) {
        toast.warning(extractErrorMessage(err));
      } else {
        toast.error(extractErrorMessage(err));
      }
    },
  });
}

export function useDispatchConfig() {
  return useQuery({
    queryKey: ['uplog', 'dispatch-config'],
    queryFn: () => uplogApi.getDispatchConfig(),
    select: (res) => res.data.data,
  });
}

export function useUpdateDispatchConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uplogApi.updateDispatchConfig,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['uplog', 'dispatch-config'] });
      toast.success('Configuração de disparo salva');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useDispatchLog(params?: { page?: number; pageSize?: number; search?: string }) {
  return useQuery({
    queryKey: ['uplog', 'dispatch-log', params],
    queryFn: () => uplogApi.getDispatchLog(params),
    select: (res) => ({ data: res.data.data, meta: res.data.meta }),
    refetchInterval: 30_000,
  });
}

export function useTestDispatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documento?: string) => uplogApi.testDispatch(documento),
    onSuccess: (res) => {
      const { documento, mes, ano, phone } = res.data.data;
      toast.success(`Disparo enviado para ${phone} — doc. ${documento} (${mes}/${ano})`);
      void qc.invalidateQueries({ queryKey: ['uplog', 'dispatch-log'] });
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useDispatchEvents(logId: string | null) {
  return useQuery({
    queryKey: ['uplog', 'dispatch-events', logId],
    queryFn: () => uplogApi.getDispatchEvents(logId!),
    select: (res) => res.data.data,
    enabled: !!logId,
  });
}


export function useUplogDashboardStats(period: string) {
  return useQuery({
    queryKey: ['uplog', 'dashboard-stats', period],
    queryFn: () => uplogApi.getDashboardStats(period),
    select: (res) => res.data.data,
    refetchInterval: 60_000,
  });
}

export function usePendencias(params?: { page?: number; pageSize?: number; search?: string }) {
  return useQuery({
    queryKey: ['uplog', 'pendencias', params],
    queryFn: () => uplogApi.getPendencias(params),
    select: (res) => ({ data: res.data.data, meta: res.data.meta }),
    refetchInterval: 30_000,
  });
}
